import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Award, Plus, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso || ""; }
}

export default function AdminLoyalty() {
  const [data, setData] = useState({ items: [], count: 0, reward_threshold: 5, eligible_now: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [punchEmail, setPunchEmail] = useState("");
  const [punchName, setPunchName] = useState("");
  const [punchNote, setPunchNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/loyalty`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.items;
    return data.items.filter((r) =>
      (r.email || "").toLowerCase().includes(needle) ||
      (r.name || "").toLowerCase().includes(needle),
    );
  }, [data.items, q]);

  const addPunch = async (e) => {
    e.preventDefault();
    if (!punchEmail.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/loyalty/punch`, {
        email: punchEmail.trim(), name: punchName.trim(), note: punchNote.trim(),
      }, { withCredentials: true });
      toast.success("Punch added.");
      setPunchEmail(""); setPunchName(""); setPunchNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add punch");
    } finally { setBusy(false); }
  };

  const addPunchFor = async (email, name) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/loyalty/punch`,
        { email, name: name || "", note: "quick add" },
        { withCredentials: true });
      toast.success(`+1 punch for ${email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const award = async (email) => {
    if (!window.confirm(`Award a free 30-minute facial to ${email}?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/loyalty/award`,
        { email, reward: "Free 30-minute facial" },
        { withCredentials: true });
      toast.success("Reward awarded. Punches reset.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to award");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-loyalty">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">Loyalty · Punch Card</h2>
          <p className="text-sm text-[#0F1F4E]/70 mt-1 max-w-xl font-light">
            Every booking counts as one punch. After {data.reward_threshold} punches,
            the client earns a free 30-minute facial. Add manual punches for phone-booked visits.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span data-testid="loyalty-count-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2EBD3] border border-[#D9CFA0] text-xs uppercase tracking-wider">
            {data.count} clients tracked
          </span>
          <span data-testid="loyalty-eligible-badge" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider ${data.eligible_now > 0 ? "bg-[#EAD584] text-[#0F1F4E]" : "bg-[#F2EBD3] border border-[#D9CFA0]"}`}>
            <Award className="w-3.5 h-3.5" /> {data.eligible_now} eligible now
          </span>
        </div>
      </div>

      <form onSubmit={addPunch} data-testid="loyalty-add-punch-form" className="bg-white border border-[#D9CFA0] rounded-lg p-5 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <label className="block md:col-span-1">
          <span className="overline block mb-1.5">Email</span>
          <input required type="email" value={punchEmail} onChange={(e) => setPunchEmail(e.target.value)} data-testid="loyalty-punch-email" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="client@email.com" />
        </label>
        <label className="block md:col-span-1">
          <span className="overline block mb-1.5">Name (optional)</span>
          <input type="text" value={punchName} onChange={(e) => setPunchName(e.target.value)} data-testid="loyalty-punch-name" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="Their name" />
        </label>
        <label className="block md:col-span-1">
          <span className="overline block mb-1.5">Note (optional)</span>
          <input type="text" value={punchNote} onChange={(e) => setPunchNote(e.target.value)} data-testid="loyalty-punch-note" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="Phone booking · Oct 12" />
        </label>
        <div className="md:col-span-1 flex items-end">
          <button type="submit" disabled={busy || !punchEmail.trim()} data-testid="loyalty-punch-submit" className="btn-shine inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#FAF6E8] hover:bg-[#1B3370] transition-colors disabled:opacity-50 w-full justify-center">
            <Plus className="w-3.5 h-3.5" /> Add punch
          </button>
        </div>
      </form>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0F1F4E]/40" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="loyalty-filter" placeholder="Filter by email or name…" className="w-full bg-white border border-[#D9CFA0] rounded-md pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#0F1F4E]" />
      </div>

      {err && <p className="text-red-600 text-sm mb-4">{err}</p>}
      {loading ? <p className="text-[#0F1F4E]/70">Loading…</p> : filtered.length === 0 ? (
        <p className="text-[#0F1F4E]/70" data-testid="loyalty-empty">No clients yet — the first booking will start the counter.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#D9CFA0] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EBD3]">
              <tr>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Client</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Auto</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Manual</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Awarded</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Current</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.email} data-testid={`loyalty-row-${r.email}`} className="border-t border-[#D9CFA0]/60">
                  <td className="px-4 py-3">
                    <div className="font-serif text-base">{r.name || "—"}</div>
                    <a href={`mailto:${r.email}`} className="text-xs underline text-[#0F1F4E]/70 break-all">{r.email}</a>
                    {r.last_booking_at && <div className="text-[10px] uppercase tracking-wider text-[#0F1F4E]/50 mt-1">Last visit {fmtDate(r.last_booking_at)}</div>}
                  </td>
                  <td className="px-4 py-3 text-center tabular-nums">{r.auto_count}</td>
                  <td className="px-4 py-3 text-center tabular-nums">{r.manual_count}</td>
                  <td className="px-4 py-3 text-center tabular-nums">{r.awarded_count}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-sm font-medium tabular-nums ${r.eligible_for_reward ? "bg-[#EAD584] text-[#0F1F4E]" : "bg-[#F2EBD3] text-[#0F1F4E]/80"}`}>
                      {r.current_punches} / {r.reward_threshold}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => addPunchFor(r.email, r.name)} disabled={busy} data-testid={`loyalty-quick-punch-${r.email}`} className="text-xs uppercase tracking-wider text-[#0F1F4E] hover:text-[#C9A65A] underline underline-offset-4 mr-3">+ Punch</button>
                    <button type="button" onClick={() => award(r.email)} disabled={busy || !r.eligible_for_reward} data-testid={`loyalty-award-${r.email}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] hover:bg-[#1B3370] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                      <Award className="w-3 h-3" /> Award
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
